import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    NEW, IN_PROGRESS, SUCCEEDED, FAILED,
    STORAGE_AWS,
    STORAGE_GOOGLE,
    STORAGE_MICROSOFT,
    IDLE, PENDING,
} from "const"
import { MtDriveFileCreate } from "server/models"
import { StateType, randomString } from "client/utils"
import {
    StoreType,
    NewFile,
    NewFileValidation,

    UploadFile,
    AddDriveFile,

    setDriveUploadFileList,
    setDriveUploadFileListStatus,
    setDriveFileReloadList,

    parseName,
    initFileCreate,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    FormGroup,
    Label,
    Input,
    FileLoader,
    DivInput,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

import {
    FileWrapper,
    FileStatusWrapper,
    FileStatusLoaderWrapper,
    FileStatusSuccess,
    FileStatusError,
} from "./styled"

const emptyFile: NewFile = {
    tempIndex: "",
    name: "",
    revision: "0",
    file: null,
    status: NEW,
}

const FileAdd = () => {
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const fileList = useSelector((state: StoreType) => state.drive.uploadFileList)

    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const { defaultStorage } = accountSettings.drive

    const [validation, setValidation] = useState<{ [key: string]: NewFileValidation }>({})
    const [uploadState, setUploadState] = useState<StateType>(IDLE)

    useEffect(() => {
        const empty: NewFile = {
            ...emptyFile,
            tempIndex: new Date().getTime() + randomString(30),
        }
        dispatch(setDriveUploadFileList([empty]))
    }, [])

    useEffect(() => {
        if (uploadState === IDLE) return
        let uploadCount = 0
        for (let i = 0; i < fileList.length; i++) {
            const { status } = fileList[i]
            if (status === SUCCEEDED || status === FAILED) uploadCount += 1
            else break
        }
        if (uploadCount === fileList.length) {
            setUploadState(IDLE)
            dispatch(setDriveFileReloadList(false))
            setTimeout(() => {
                dispatch(setDriveFileReloadList(true))
            }, 0)
        }
    }, [fileList, uploadState])

    const onChangeName = (index: number, value: string) => {
        const newFileList: NewFile[] = [...fileList]
        newFileList[index] = {
            ...newFileList[index],
            name: value.slice(0, 255),
        }
        dispatch(setDriveUploadFileList(newFileList))
    }

    const onChangeRevision = (index: number, value: string) => {
        const newFileList: NewFile[] = [...fileList]
        newFileList[index] = {
            ...newFileList[index],
            revision: value.slice(0, 100),
        }
        dispatch(setDriveUploadFileList(newFileList))
    }

    const onChangeFile = (index: number, file: File) => {
        const newFileList: NewFile[] = [...fileList]
        newFileList[index] = {
            ...newFileList[index],
            file,
            name: file ? file.name : "",
        }
        dispatch(setDriveUploadFileList(newFileList))
    }

    const onDeleteFile = (index: number) => {
        const newFileList: NewFile[] = [...fileList]
        newFileList.splice(index, 1) // removes [index] element
        dispatch(setDriveUploadFileList(newFileList))
    }

    const onAddSingle = () => { // add empty file to the list
        const newFileList: NewFile[] = [...fileList]
        const empty: NewFile = {
            ...emptyFile,
            tempIndex: new Date().getTime() + randomString(30),
        }
        newFileList.push(empty)
        dispatch(setDriveUploadFileList(newFileList))
    }

    const onAddSeveral = (files: FileList) => { // select several from hard drive
        const newFileList: NewFile[] = [...fileList]
        Object.values(files).forEach((file) => {
            const newFile: NewFile = {
                ...emptyFile,
                tempIndex: new Date().getTime() + randomString(30),
                name: file.name,
                file,
            }
            newFileList.push(newFile)
        })
        dispatch(setDriveUploadFileList(newFileList))
    }

    const onUpload = () => {
        const newFileList: NewFile[] = [...fileList]
        const fileListValidation: { [key: string]: NewFileValidation } = {}

        newFileList.forEach((row, index) => {
            const fileName = row.name.trim()
            const fileRevision = row.revision.trim()
            newFileList[index] = {
                ...row,
                name: fileName,
                revision: fileRevision,
            }
            if (!fileName.length || !fileRevision.length) {
                fileListValidation[row.tempIndex] = {
                    name: !fileName.length ? lang.validationFieldRequired : null,
                    revision: !fileRevision.length ? lang.validationFieldRequired : null,
                }
            }
        })

        setValidation(fileListValidation)
        if (Object.keys(fileListValidation).length) return


        setUploadState(PENDING)
        const targetFolderId = drivePath.length ? drivePath[drivePath.length - 1].id : 0

        newFileList.forEach((row, index) => {
            const { name, revision, file, status } = row

            if (status !== SUCCEEDED && file) {
                dispatch(setDriveUploadFileListStatus(index, IN_PROGRESS))

                UploadFile({
                    file,
                    fileName: name,
                    revisionNumber: revision,
                    storage: defaultStorage,
                    userData,
                    projectId: null,
                    targetFolderId,
                    callbackSuccess: () => {
                        dispatch(setDriveUploadFileListStatus(index, SUCCEEDED))
                    },
                    callbackError: () => {
                        dispatch(setDriveUploadFileListStatus(index, FAILED))
                    },
                })
            }

            if (status !== SUCCEEDED && !file) {
                dispatch(setDriveUploadFileListStatus(index, IN_PROGRESS))

                const nameParsed = parseName(name)
                const driveFile: MtDriveFileCreate = {
                    ...initFileCreate,
                    folderId: targetFolderId,
                    accountId: userData.accountId,
                    owner: userData.userId,
                    name: nameParsed.name,
                    copy: nameParsed.counter,
                    storage: defaultStorage,
                }
                AddDriveFile(driveFile, null).then((newFileId) => {
                    if (newFileId) dispatch(setDriveUploadFileListStatus(index, SUCCEEDED))
                    else dispatch(setDriveUploadFileListStatus(index, FAILED))
                })
            }
        })
    }

    let storageTitle = ""
    if (defaultStorage === STORAGE_AWS) storageTitle = lang.aws
    if (defaultStorage === STORAGE_GOOGLE) storageTitle = lang.google
    if (defaultStorage === STORAGE_MICROSOFT) storageTitle = lang.microsoftOneDrive

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.addFile_s}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageForm>
                    {
                        fileList.map((file, index) => (
                            <FileWrapper key={file.tempIndex}>
                                <Row>
                                    <Col xs={6}>
                                        <FormGroup>
                                            <Label>{lang.name}</Label>
                                            <Input
                                                type="text"
                                                value={file.name}
                                                onChange={(e) => onChangeName(index, e.target.value)}
                                            />
                                            <Feedback>
                                                {validation && validation[file.tempIndex] && validation[file.tempIndex].name}
                                            </Feedback>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>{lang.revision}</Label>
                                            <Input
                                                type="text"
                                                value={file.revision}
                                                onChange={(e) => onChangeRevision(index, e.target.value)}
                                            />
                                            <Feedback>
                                                {validation && validation[file.tempIndex] && validation[file.tempIndex].revision}
                                            </Feedback>
                                        </FormGroup>
                                    </Col>
                                    <Col xs={6}>
                                        <FormGroup>
                                            <Label>{lang.file}</Label>
                                            <FileLoader
                                                type="any"
                                                value={file.file}
                                                autoLoad={false}
                                                onLoad={(newFile) => onChangeFile(index, newFile)}
                                                callback={(newFile) => { // clear file on delete
                                                    if (!newFile) onChangeFile(index, null)
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <FileStatusWrapper>
                                    { file.status === IN_PROGRESS && <FileStatusLoaderWrapper><SpinnerIcon /></FileStatusLoaderWrapper> }
                                    { file.status === SUCCEEDED && <FileStatusSuccess>{lang.uploaded}</FileStatusSuccess> }
                                    { file.status === FAILED && <FileStatusError>{lang.uploadError}</FileStatusError> }
                                    {
                                        (file.status === NEW || file.status === FAILED)
                                        && <Button onClick={() => onDeleteFile(index)}>{lang.delete}</Button>
                                    }
                                </FileStatusWrapper>
                            </FileWrapper>
                        ))
                    }
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.storage}</Label>
                                <DivInput widePadding>{storageTitle}</DivInput>
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <Button variant="success" row onClick={onAddSingle}>{`${lang.add} +`}</Button>
                            <Button variant="success" row>
                                <input
                                    type="file"
                                    multiple
                                    title={lang.selectFiles}
                                    onChange={(e) => {
                                        onAddSeveral(e.target.files)
                                        e.target.value = null
                                    }}
                                />
                                {`${lang.addSeveral} ++`}
                            </Button>
                        </Col>
                        <Col xs={12}>
                            <Button
                                variant="success"
                                disabled={uploadState === PENDING}
                                onClick={onUpload}
                            >
                                {uploadState === PENDING ? <SpinnerIcon /> : lang.upload}
                            </Button>
                        </Col>
                    </Row>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default FileAdd
